import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterLink, RouterOutlet } from '@angular/router';
import { FirebaseService } from './common/firebase/firebase.service';
import { AsyncPipe, CurrencyPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  activeNavItem,
  AppArea,
  AppNavItem,
  areaForUrl,
  isShoutPlusPitchUrl,
  NAV,
  SHOUT_PLUS_INTRO_PATH,
  NavAccess,
  rememberArea,
  rememberTab,
  resolveAreaEntry,
  visibleNavItems,
} from './common/app-config/app-nav';
import { ManageBriefTabsService } from './pages/manage-brief-tabs/service/manage-brief-tabs/manage-brief-tabs.service';
import { isShoutPlusBrief } from './shout-plus/utils/shout-plus-hire.util';
import { isShoutPlusAffiliateEnabled } from './common/app-config/shout-plus-feature-flags';
import { SHOUT_PLUS_ADMIN_ACCESS_ROLES } from './shared/guards/shout-plus-admin/shout-plus-admin.guard';
import { CurrentUserService } from './common/current-user/current-user.service';
import { hasSupportAdminAccess } from './common/support-admin-roles.util';
import { ImageService } from './services/image/image.service';
import { combineLatest, filter, take } from 'rxjs';
import { WalletService } from './services/wallet/wallet.service';
import { TopUpDrawerComponent } from './pages/wallet/components/top-up-drawer/top-up-drawer.component';
import { NotificationsDrawerComponent } from './atoms/notifications-drawer/notifications-drawer.component';
import { GetStreamService } from './services/get-stream/get-stream.service';
import { Location } from '@angular/common';
import { ChoosePlanService } from './pages/choose-plan/service/choose-plan/choose-plan.service';
import { ChoosePlan } from './pages/choose-plan/choose-plan.component';
import { ChooseAutoPilotComponent } from './pages/choose-auto-pilot/choose-auto-pilot.component';
import { ChoosePlanBoltOnAddComponent } from './pages/choose-plan-bolt-on-add/choose-plan-bolt-on-add.component';
import { DownloadProgressToastComponent } from './atoms/download-progress-toast/download-progress-toast.component';
import { RefreshResultsToast } from './atoms/refresh-results-toast/refresh-results-toast';
import { AppUpdateBanner } from './atoms/app-update-banner/app-update-banner';
import { BrandSwitchTransition } from './atoms/brand-switch-transition/brand-switch-transition';
import Clarity from '@microsoft/clarity';
import { environment } from '@/environments/environment';
import { clearStalePendingSubscription } from './services/analytics/customer-analytics';
import {
  POSTHOG_ENVIRONMENT_PROPERTY,
  stampPosthogEnvironment,
} from './services/analytics/posthog-environment';
import { PosthogService } from './services/analytics/posthog.service';
import { AnalyticsSessionService } from './services/analytics/analytics-session.service';
import { MarketingAttributionService } from './services/analytics/marketing-attribution';
import { GaService } from './services/ga/ga';
import { HasRoleDirectiveDirective } from "./common/directives/has-role/has-role-directive.directive";
import { TooltipDirective } from './common/directives/tooltip/tooltip.directive';
import { ThemeService } from './services/theme/theme.service';
import { AiChatSidebarComponent } from './ai-guide/components/ai-chat-sidebar/ai-chat-sidebar.component';
import { GuideOverlayComponent } from './ai-guide/components/guide-overlay/guide-overlay.component';
import { LaunchGuideButton } from './ai-guide/components/launch-guide-button/launch-guide-button';
import { GuideControls } from './ai-guide/components/guide-controls/guide-controls';
import { SupportService } from './services/support/support.service';
import { isPublicUnauthenticatedPath } from './common/support-chat.util';
import { isValueFirstSignupPath } from './common/signup-entry.util';
import { AiGuideChatService } from './ai-guide/services/ai-guide-chat.service';
import { AutoDeclineExplainer } from './atoms/auto-decline-explainer/auto-decline-explainer';
import { AutoDeclineExplainerService } from './services/auto-decline-explainer/auto-decline-explainer.service';
import { AdConversionsService } from '@/app/services/analytics/ad-conversions.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AsyncPipe, NgClass, NgTemplateOutlet, TopUpDrawerComponent,
    CurrencyPipe, NotificationsDrawerComponent, ChoosePlan,
    ChoosePlanBoltOnAddComponent, DownloadProgressToastComponent, RefreshResultsToast, AppUpdateBanner,
    BrandSwitchTransition,
    RouterLink, HasRoleDirectiveDirective, TooltipDirective,
    AiChatSidebarComponent, GuideOverlayComponent, LaunchGuideButton, GuideControls,
    RouterLink, HasRoleDirectiveDirective, TooltipDirective, AutoDeclineExplainer],
  templateUrl: './app.html',
  styleUrl: './app.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    '(document:keydown.escape)': 'closeMobileNav()',
  },
})
export class App {
  protected readonly title = signal('shaas21-pillars');
  protected readonly hideAreaTabs = signal(false);
  readonly supportService = inject(SupportService);
  protected get canAccessSandboxBilling(): boolean {
    return this.currentUserService.canAccessSandboxBilling();
  }
  protected get canAccessShoutPlus(): boolean {
    return this.currentUserService.hasShoutPlusAccess();
  }

  private focussedBrand = signal<any | undefined>(undefined);
  private lastNavbarWalletBrandId: string | undefined;

  walletButtonOpen = signal<boolean>(false);
  addPaymentUrl = signal<string | undefined>(undefined);
  mobileNavOpen = signal(false);
  /** Studio accounts can preview the SHOUT+ tabs without entering the product. */
  private readonly previewArea = signal<AppArea>('studio');

  private posthogService = inject(PosthogService);
  private adConversions = inject(AdConversionsService);
  private analyticsSessionService = inject(AnalyticsSessionService);
  private readonly marketingAttribution = inject(MarketingAttributionService);
  private readonly currentUrl = signal(inject(Router).url);
  private readonly manageBriefTabsService = inject(ManageBriefTabsService);

  /** Area of the last page outside /manage-brief, used while a brief is still loading. */
  private readonly areaBeforeBrief = signal<AppArea>('studio');

  /** SHOUT+ briefs open at /manage-brief/…, outside /shout-plus, but still belong to the SHOUT+ area. */
  private readonly onShoutPlusBriefPage = computed(() => {
    const path = this.currentUrl().split('?')[0] ?? '';
    if (!/^\/manage-brief(\/|$)/.test(path)) {
      return false;
    }
    const briefId = /^\/manage-brief\/([^/#]+)/.exec(path)?.[1];
    const brief = this.manageBriefTabsService.focussedBrief();
    if (brief && (!briefId || brief.id === briefId)) {
      return isShoutPlusBrief(brief);
    }
    // Brief not loaded yet (or the previous one is still in memory): stay in the area we came from.
    return this.areaBeforeBrief() === 'shout-plus';
  });

  protected readonly activeArea = computed(() =>
    this.onShoutPlusBriefPage() || isShoutPlusPitchUrl(this.currentUrl()) ? 'shout-plus' : areaForUrl(this.currentUrl()),
  );
  readonly autoDeclineExplainer = inject(AutoDeclineExplainerService);

  protected readonly showUserProfileHostBg = computed(() => {
    const path = this.currentUrl().split('?')[0] ?? '';
    return /^\/admin-options\/user-profile(\/|$)/.test(path);
  });

  protected readonly mainRouteHostBackgroundClass = computed((): string => {
    if (this.showUserProfileHostBg()) {
      return 'app-main-route-host-user-profile-bg';
    }
    if (isShoutPlusPitchUrl(this.currentUrl())) {
      return 'bg-black';
    }
    return 'bg-white dark:bg-zinc-900';
  });

  constructor(
    private router: Router,
    private location: Location,
    public firebaseService: FirebaseService,
    private sanitizer: DomSanitizer,
    public currentUserService: CurrentUserService,
    public imageService: ImageService,
    public walletService: WalletService,
    public getStreamService: GetStreamService,
    public choosePlanService: ChoosePlanService,
    public themeService: ThemeService,
    private _gaService: GaService,
    public aiGuideChatService: AiGuideChatService
  ) {

    this.initAnalyticTools();
    this.trackPosthogPageLifecycle();
    this.hideAreaTabs.set(this.routeHidesAreaNav());
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = (event as NavigationEnd).urlAfterRedirects;
        if (!/^\/manage-brief(\/|$)/.test(url.split('?')[0] ?? '')) {
          this.areaBeforeBrief.set(areaForUrl(url));
        }
        this.currentUrl.set(url);
        this.closeMobileNav();
        this.hideAreaTabs.set(this.routeHidesAreaNav());
        if (!this.canAccessShoutPlus && !isShoutPlusPitchUrl(url)) {
          this.previewArea.set('studio');
        }
        this.rememberNavigation(url);
      });

    const targetUrl = this.location.path();
    console.log('<-------------------------------->');
    console.log('App Component Constructor');
    console.log('Target URL:', targetUrl);
    console.log('<-------------------------------->');
    this.firebaseService.targetUrl = targetUrl;
    const publicUnauthPath = isPublicUnauthenticatedPath(targetUrl);
    const signupEntry = isValueFirstSignupPath(targetUrl);

    // Magic-link ticket threads / auth-action / value-first signup stay on their URL.
    if (!publicUnauthPath && !signupEntry) {
      this.router.navigate(['/loading'], {
        queryParams: this.marketingAttribution.toQueryParams(),
        queryParamsHandling: 'merge',
      });
    }

    // Only react after Firebase has determined auth state; go to onboarding only when there is no valid user
    combineLatest([
      this.firebaseService.authStateChecked$,
      this.firebaseService.user$,
    ]).pipe(
      filter(([checked]) => checked),
      take(1),
    ).subscribe(([, user]) => {
      console.log('👮🏻‍♂️👮🏻‍♂️ Auth state resolved, user:', user);
      if (!user) {
        if (
          publicUnauthPath
          || signupEntry
          || isPublicUnauthenticatedPath(this.location.path())
          || isValueFirstSignupPath(this.location.path())
        ) {
          return;
        }
        // this.router.navigate(['/onboarding']);
        this.router.navigate(['/onboarding-select'], {
          queryParams: this.marketingAttribution.toQueryParams(),
          queryParamsHandling: 'merge',
        });
      }
      // When user is present, navigation is handled by FirebaseService.launchApplication()
    });

    this.firebaseService.user$.subscribe((user) => {
      if (user) {
        this.rememberNavigation(this.router.url);
      }
    });

    // Subscribe to focussedBrand$ and update the signal
    this.currentUserService.focussedBrand$.subscribe((brand) => {
      console.log('👮🏻‍♂️👮🏻‍♂️ Focussed Brand CHANGE:', brand);
      this.focussedBrand.set(brand);
      if (!brand?.id) {
        this.autoDeclineExplainer.resetSession();
      }
    });

    effect(() => {
      const brand = this.focussedBrand();
      const ready =
        this.currentUserService.onboardingComplete() && this.currentUserService.subscriptionActive();
      if (!ready || !brand?.id) {
        return;
      }
      this.autoDeclineExplainer.openIfUnseen(brand);
    });

    effect(() => {
      const roles = this.currentUserService.userRoles();
      if (hasSupportAdminAccess(roles)) {
        this.supportService.refreshOpenTicketCount();
      }
    });

    this.currentUserService.shoutUser$.subscribe((user) => {
      if (!user) {
        this.supportService.unreadUserTicketCount.set(0);
        return;
      }

      this.supportService.refreshUnreadUserTicketCount();

      const roles = user.accessRoles ?? this.currentUserService.userRoles();
      if (hasSupportAdminAccess(roles)) {
        this.supportService.refreshOpenTicketCount();
      }
    });

    effect(() => {
      const brandId = this.focussedBrand()?.id as string | undefined;
      if (!brandId || brandId === this.lastNavbarWalletBrandId) {
        return;
      }
      this.lastNavbarWalletBrandId = brandId;
      this.walletService.getBalance(brandId).pipe(take(1)).subscribe();
    });

    this.closeAiGuideBelowLargeBreakpoint();
  }

  /** Collapse the AI panel when the viewport drops below the lg breakpoint. */
  private closeAiGuideBelowLargeBreakpoint(): void {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const closeIfNeeded = (): void => {
      if (!mediaQuery.matches && this.aiGuideChatService.isAiGuideOpen()) {
        this.setAiGuideOpen(false);
      }
    };
    closeIfNeeded();
    mediaQuery.addEventListener('change', closeIfNeeded);
  }

  setAiGuideOpen(state: boolean) {
    this.aiGuideChatService.isAiGuideOpen.set(state);
  }

  async warmPaymentLinks() {
    const customerIdentifiers = this.getStripeCustomerIdentifiers();
    for (const identifier of customerIdentifiers) {
      const approvalLink = await this.currentUserService.prefetchApprovalLink(identifier, 7);
      console.log('🤑🤑🤑 approvalLink', approvalLink);
      if (approvalLink) {
        this.addPaymentUrl.set(approvalLink);
      }
    }
  }

  private getStripeCustomerIdentifiers(): string[] {
    const subscription = this.currentUserService.currentSubscription();
    const subscriptionCustomer =
      typeof subscription?.customer === 'string'
        ? subscription.customer
        : subscription?.customer?.id;

    const candidates = [
      this.currentUserService.focussedBrand?.stripeCustomerId ||
      subscriptionCustomer
    ];

    return Array.from(new Set(candidates.filter((value): value is string => !!value && value.length > 0)));
  }

  selectNavItem(item: AppNavItem): void {
    this.walletButtonOpen.set(false);
    this.closeMobileNav();
    void this.router.navigate([item.route]);
  }

  chooseArea(area: AppArea): void {
    this.walletButtonOpen.set(false);
    if (!this.canAccessShoutPlus) {
      this.previewArea.set(area);
      this.closeMobileNav();
      if (area === 'shout-plus') {
        this.openShoutPlusIntro();
        return;
      }
      if (isShoutPlusPitchUrl(this.router.url)) {
        void this.router.navigate(['/dashboard']);
      }
      return;
    }
    this.closeMobileNav();
    this.switchArea(area);
  }

  switchArea(area: AppArea): void {
    const items = area === 'shout-plus' ? this.shoutPlusTabs() : this.studioTabs();
    const route = resolveAreaEntry(area, items);
    void this.router.navigate([route]);
  }

  selectAreaTab(item: AppNavItem): void {
    if (!this.canAccessShoutPlus && this.displayedArea() === 'shout-plus') {
      this.advertiseShoutPlus();
      return;
    }
    this.selectNavItem(item);
  }

  toggleMobileNav(): void {
    this.mobileNavOpen.update((open) => !open);
  }

  closeMobileNav(): void {
    this.mobileNavOpen.set(false);
  }

  goToDashboard(): void {
    this.closeMobileNav();
    if (!this.canAccessShoutPlus) {
      this.previewArea.set('studio');
      void this.router.navigate(['/dashboard']);
      return;
    }
    if (this.activeArea() === 'shout-plus') {
      void this.router.navigate(['/shout-plus']);
      return;
    }
    void this.router.navigate(['/dashboard']);
  }

  goToANonTab(route: string): void {
    this.walletButtonOpen.set(false);
    this.closeMobileNav();
    void this.router.navigate([route]);
  }

  /** Studio accounts can see SHOUT+ tabs. Choosing one opens a short intro, not the plan picker. */
  advertiseShoutPlus(): void {
    this.previewArea.set('shout-plus');
    this.openShoutPlusIntro();
  }

  private openShoutPlusIntro(): void {
    this.closeMobileNav();
    this.choosePlanService.closeAllModals();
    void this.router.navigate([SHOUT_PLUS_INTRO_PATH]);
  }

  protected isShoutPlusShell(): boolean {
    const path = this.currentUrl().split('?')[0] ?? '';
    return path === '/shout-plus' || path.startsWith('/shout-plus/') || this.onShoutPlusBriefPage();
  }

  /** The app bar is black inside SHOUT+ and on its intro page (both themes); Studio keeps the light/dark theme bar. */
  protected usesDarkAppHeader(): boolean {
    return this.isShoutPlusShell() || isShoutPlusPitchUrl(this.currentUrl());
  }

  protected headerClass(): string {
    return this.usesDarkAppHeader() ? 'shout-plus-top-nav bg-black h-16' : 'h-16';
  }

  protected showFullNavigation(): boolean {
    return this.currentUserService.subscriptionActive();
  }

  protected studioTabs(): AppNavItem[] {
    return visibleNavItems(NAV.studio, this.navAccess());
  }

  protected shoutPlusTabs(): AppNavItem[] {
    return visibleNavItems(NAV.shoutPlus, this.navAccess());
  }

  protected isTabActive(item: AppNavItem): boolean {
    const items = this.canAccessShoutPlus && this.activeArea() === 'shout-plus'
      ? this.shoutPlusTabs()
      : this.studioTabs();
    // A SHOUT+ brief lives under the SHOUT+ Briefs tab.
    const url = this.onShoutPlusBriefPage() ? '/shout-plus/briefs' : this.currentUrl();
    return activeNavItem(items, url)?.id === item.id;
  }

  /** `bar` is the single studio-only header row. `row` is the SHOUT+ account tab row. */
  protected tabButtonClass(item: AppNavItem, placement: 'bar' | 'row'): string {
    const active = this.isTabActive(item);
    const pad = placement === 'row' ? 'py-3' : 'py-5';
    if (placement === 'bar') {
      if (this.usesDarkAppHeader()) {
        return active
          ? `${pad} border-white font-semibold text-white focus-visible:outline-white`
          : `${pad} border-transparent font-medium text-white/80 hover:border-white/40 hover:text-white focus-visible:outline-white`;
      }
      return active
        ? `${pad} border-zinc-950 font-semibold text-primary focus-visible:outline-primary dark:border-white`
        : `${pad} border-transparent font-medium text-secondary hover:border-white/20 hover:text-gray-200 focus-visible:outline-primary`;
    }
    if (this.displayedArea() === 'shout-plus' && !this.canAccessShoutPlus) {
      return `${pad} border-transparent font-medium text-zinc-800 hover:border-amber-400 hover:text-amber-700 focus-visible:outline-amber-500 dark:text-zinc-100 dark:hover:text-amber-300`;
    }
    if (this.isShoutPlusShell()) {
      return active
        ? `${pad} border-amber-400 font-semibold text-white focus-visible:outline-amber-300`
        : `${pad} border-transparent font-medium text-white/70 hover:text-white focus-visible:outline-amber-300`;
    }
    return active
      ? `${pad} border-primary font-semibold text-primary focus-visible:outline-primary`
      : `${pad} border-transparent font-medium text-secondary hover:text-primary focus-visible:outline-primary`;
  }

  protected displayedArea(): AppArea {
    if (this.canAccessShoutPlus) {
      return this.activeArea();
    }
    if (isShoutPlusPitchUrl(this.currentUrl())) {
      return 'shout-plus';
    }
    return this.previewArea();
  }

  protected areaTagline(): string {
    return this.displayedArea() === 'shout-plus'
      ? 'Turn your customers or employees into creators'
      : '25,000 creators at the ready';
  }

  protected shoutPlusSwitcherClass(): string {
    const base = 'rounded-full px-3 py-1.5 text-sm cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';
    if (this.displayedArea() === 'shout-plus') {
      return `${base} shout-plus-gold-shadow bg-amber-400 font-semibold text-zinc-950`;
    }
    return this.usesDarkAppHeader()
      ? `${base} font-medium text-white/75 hover:bg-white/10 hover:text-white`
      : `${base} font-medium text-zinc-600 hover:bg-zinc-950/5 hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-white/10 dark:hover:text-white`;
  }

  protected studioSwitcherClass(): string {
    const base = 'rounded-full px-3 py-1.5 text-sm cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';
    if (this.displayedArea() === 'studio') {
      return `${base} bg-primary font-semibold text-primary-foreground`;
    }
    return this.usesDarkAppHeader()
      ? `${base} font-medium text-white/75 hover:bg-white/10 hover:text-white`
      : `${base} font-medium text-zinc-600 hover:bg-zinc-950/5 hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-white/10 dark:hover:text-white`;
  }

  protected areaTabsRowClass(): string {
    if (this.displayedArea() === 'shout-plus' && !this.canAccessShoutPlus) {
      return 'border-b border-amber-500/30 bg-amber-400/10';
    }
    return this.areaRowClass();
  }

  protected areaLabelClass(): string {
    const pill = 'm-0 shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold';
    if (this.displayedArea() !== 'shout-plus') {
      return `${pill} bg-primary/10 text-primary`;
    }
    return `${pill} bg-zinc-800 text-amber-400`;
  }

  protected areaRowClass(): string {
    if (this.isShoutPlusShell()) {
      return 'border-b border-white/10';
    }
    return 'border-b border-zinc-950/10 dark:border-white/10';
  }

  protected shoutPlusLocked(): boolean {
    return !this.canAccessShoutPlus && this.displayedArea() === 'shout-plus';
  }

  protected studioContentFrame(): boolean {
    if (isShoutPlusPitchUrl(this.currentUrl())) {
      return false;
    }
    return !this.isShoutPlusShell() || this.isShoutPlusBriefsList() || this.onShoutPlusBriefPage();
  }

  protected mobileTabClass(item: AppNavItem): string {
    const active = this.isTabActive(item);
    return active
      ? 'bg-zinc-100 font-semibold dark:bg-zinc-800'
      : 'font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800';
  }

  protected navIconClass(extra = ''): string {
    const tone = this.usesDarkAppHeader()
      ? 'text-white hover:text-white/80'
      : 'text-secondary hover:text-primary';
    return extra ? `${tone} ${extra}` : tone;
  }

  private navAccess(): NavAccess {
    return {
      subscriptionActive: this.showFullNavigation(),
      hasAgency: !!this.currentUserService.focussedAgency,
      affiliateEnabled: isShoutPlusAffiliateEnabled(),
      shoutPlusAdmin: this.currentUserService.hasAnyRole([...SHOUT_PLUS_ADMIN_ACCESS_ROLES]),
    };
  }

  private rememberNavigation(url: string): void {
    if (!this.canAccessShoutPlus) {
      return;
    }
    // Brief pages aren't tabs and can belong to either area, so they don't change the remembered tab.
    if (/^\/manage-brief(\/|$)/.test(url.split('?')[0] ?? '')) {
      return;
    }
    const area = areaForUrl(url);
    rememberArea(area);
    const items = area === 'shout-plus' ? NAV.shoutPlus : NAV.studio;
    const active = activeNavItem(items, url);
    if (active) {
      rememberTab(area, active.route);
    }
  }

  private routeHidesAreaNav(): boolean {
    let route = this.router.routerState.snapshot.root;
    while (route) {
      if (route.data['hideShoutPlusNav'] === true) {
        return true;
      }
      if (!route.firstChild) {
        return false;
      }
      route = route.firstChild;
    }
    return false;
  }

  private isShoutPlusBriefsList(): boolean {
    const path = (this.currentUrl().split('?')[0] ?? '').split('#')[0] ?? '';
    return path === '/shout-plus/briefs';
  }

  openTopUpDrawer() {
    if (!this.canAccessSandboxBilling) {
      return;
    }
    this.walletButtonOpen.set(false);
    this.closeMobileNav();
    this.walletService.openTopUpDrawer();
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  getSafeImageUrl(url: string | undefined | null): string {
    if (!url) return '';
    // Trim whitespace from the URL
    return url.trim();
  }

  onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    console.warn('👮🏻‍♂️👮🏻‍♂️ Image Error:', img);
  }

  getTruncatedTitle(title: string) {
    if (title.length > 30) {
      return title.substring(0, 30) + '...';
    }
    return title;
  }

  getInboxSelectedClass() {
    if (this.router.url === '/inbox') {
      return "outline-2 outline-offset-2 outline-primary"
    } else {
      return ""
    }
  }

  getInsightsSelectedClass() {
    if (this.router.url === '/insights') {
      return "outline-2 outline-offset-2 outline-primary"
    } else {
      return ""
    }
  }

  getAdminOptionsSelectedClass() {
    if (this.router.url.startsWith('/admin-options')) {
      return "outline-2 outline-offset-2 outline-primary"
    } else {
      return ""
    }
  }

  getAdminOptionsSelectedIconClass() {
    if (this.currentUserService.impersonationSession()) {
      return "text-amber-600 dark:text-amber-400"
    }
    return this.usesDarkAppHeader() ? "text-white" : "text-secondary"
  }

  getInsightsSelectedIconClass() {
    if (this.currentUserService.impersonationSession()) {
      return "text-amber-600 dark:text-amber-400"
    }
    return this.usesDarkAppHeader() ? "text-white" : "text-secondary"
  }

  getGotModeOptionsSelectedClass() {
    if (this.router.url === '/god-mode-options') {
      return "outline-2 outline-offset-2 outline-primary"
    } else {
      return ""
    }
  }

  // getGotModeOptionsSelectedIconClass() {
  //   if (this.currentUserService.impersonationSession()) {
  //     return "text-amber-600 dark:text-amber-400"
  //   } else {
  //     return "text-secondary"
  //   }
  // }

  

  getAccountSelectedClass() {
    if (this.router.url === '/my-account') {
      return "outline-2 outline-offset-2 outline-primary rounded-full"
    } else {
      return ""
    }
  }

  isDesktop() {
    return window.innerWidth > 768;
  }

  startTrial() {
    console.log('👮🏻‍♂️👮🏻‍♂️ Start Trial');
    if (this.choosePlanService.subscribeModalOpen()) {
      this.choosePlanService.subscribeModalOpen.set(false);
    } else {
      this.choosePlanService.subscribeModalOpen.set(true);
      console.log('👮🏻‍♂️👮🏻‍♂️ Modal Open:', this.choosePlanService.subscribeModalOpen());
    }
  }

  initAnalyticTools() {
    console.log('🕵️‍♀️🕵️‍♀️ Init Analytic Tools 🕵️‍♀️🕵️‍♀️');

    this.marketingAttribution.captureFromLocation();
    this._gaService.init();
    this.adConversions.init();

    const sessionId = this.analyticsSessionService.getSessionId();
    const sessionTouch = this.marketingAttribution.getSessionTouch();
    // Initialize Clarity
    Clarity.init(environment.analytics_tools.clarityProjectId);
    Clarity.setTag('shout_session_id', sessionId);
    Clarity.identify(sessionId);
    if (sessionTouch?.utm_source) {
      Clarity.setTag('session_utm_source', sessionTouch.utm_source);
    }
    if (sessionTouch?.utm_medium) {
      Clarity.setTag('session_utm_medium', sessionTouch.utm_medium);
    }
    if (sessionTouch?.utm_campaign) {
      Clarity.setTag('session_utm_campaign', sessionTouch.utm_campaign);
    }
    // Initialize PostHog. Session campaign is registered for this visit only, after auth is known.
    const analyticsEnvironment = environment.analytics_tools.environment;
    this.posthogService.init(environment.analytics_tools.postHogApiKey, {
      api_host: environment.analytics_tools.postHogApiHost,
      ui_host: environment.analytics_tools.postHogUiHost,
      capture_pageview: false,
      defaults: '2026-01-30',
      capture_exceptions: true,
      debug: false,
      before_send: (event) => stampPosthogEnvironment(event, analyticsEnvironment),
      loaded: (ph) => {
        ph.register({ [POSTHOG_ENVIRONMENT_PROPERTY]: analyticsEnvironment });
        if (!environment.analytics_tools.enabled) {
          ph.opt_out_capturing();
        } else {
          ph.opt_in_capturing();
        }
      },
    });
    this.firebaseService.authStateChecked$.pipe(
      filter((checked) => checked),
      take(1),
    ).subscribe(() => {
      const visitorType = this._gaService.trackSessionStart(!!this.firebaseService.userId);
      if (visitorType) {
        Clarity.setTag('visitor_type', visitorType);
      }
    });
    clearStalePendingSubscription(window.location.pathname);
    this.walletService.captureCompletedTopUpFromReturn();
  }

  isPublicReportPath(): boolean {
    const path = this.currentUrl().split('?')[0] ?? '';
    return path.startsWith('/r/');
  }

  private trackPosthogPageLifecycle() {
    if (!environment.analytics_tools.enabled) {
      return;
    }

    let currentUrl = window.location.href;

    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.posthogService.posthog.capture('$pageleave', {
            $current_url: currentUrl,
            $next_url: `${window.location.origin}${event.url}`,
          });
        }

        if (event instanceof NavigationEnd) {
          currentUrl = window.location.href;
          const componentName = this.getLeafRouteComponentName();
          this.posthogService.posthog.capture('$pageview', {
            $current_url: currentUrl,
            ...this._gaService.eventContext(),
            ...(componentName !== undefined ? { component_name: componentName } : {}),
          });
        }
      });

    window.addEventListener('pagehide', () => {
      this.posthogService.posthog.capture('$pageleave', {
        $current_url: window.location.href,
      });
    });
  }

  /**
   * Name of the routed component class for the leaf activated route (primary outlet).
   */
  private getLeafRouteComponentName(): string | undefined {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const comp = route.component;
    if (comp === null || comp === undefined) {
      return undefined;
    }
    if (typeof comp === 'function') {
      const name = comp.name;
      return name.length > 0 ? name : undefined;
    }
    return undefined;
  }

  addPaymentMethod() {
    console.log('🤑🤑🤑 addPaymentMethod() 🤑🤑🤑');
    this._gaService.trackOnboardingEvent('add_payment_method_clicked');
    window.open(this.addPaymentUrl(), '_blank');
  }

  async stopImpersonating() {
    await this.firebaseService.stopImpersonating();
  }
}
